<script>
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Form from "@/components/molecules/form";
import Button from "@/components/atoms/button";
import LazyImage from "@/components/atoms/lazy-image";
import Divider from "@/components/atoms/divider";
import StateOverlay from "@/components/molecules/state-overlay";
import Badge from "@/components/atoms/badge";
import Icon from "@/components/atoms/icon";
import { LayoutTypes } from "@/services/context/layout-helper";

export default {
  name: "AddToListeDeCourse",
  components: {
    FormSelect,
    FormSelectOption,
    FormInput,
    FormGroup,
    Form,
    Button,
    LazyImage,
    Divider,
    StateOverlay,
    Badge,
    Icon
  },
  props: {
    listes: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    article: {
      type: Object,
      required: true
    },
    layout: {
      type: String,
      required: false,
      default: "default"
    },
    ...formValidationProps()
  },
  emits: ["addToNewList", "addToList"],
  setup(props) {
    const {
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states,
      isSuccess
    } = useFormValidation(props);

    const formSelect = ref(null);

    return {
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states,
      isSuccess,
      formSelect
    };
  },
  validations() {
    return {
      titre: {
        listeOrTitreRequired() {
          return this.liste || this.titre;
        }
      }
    };
  },
  computed: {
    computedTitre() {
      if (this.liste) {
        const ldc = this.listes.find((l) => l.id === this.liste);
        return ldc.attributes.titre;
      }
      return this.titre;
    }
  },
  data() {
    return {
      liste: null,
      titre: ""
    };
  },
  methods: {
    saveListe() {
      if (this.checkValidation(this.v$)) {
        if (this.titre) {
          this.$emit("addToNewList", this.titre);
        } else {
          this.$emit("addToList", this.liste);
        }
      }
    },
    isMobile() {
      return this.layout === LayoutTypes.mobile;
    }
  }
};
</script>

<template>
  <StateOverlay
    overlay
    :is-loading="state === states.LOADING"
  >
    <template v-if="isSuccess">
      <div class="u-text-center">
        <Badge
          variant="success"
          circle
          size="xlg"
          class="u-mb-sm"
        >
          <Icon
            name="check"
            variant="white"
          />
        </Badge>
      </div>
      <p class="u-font-15 u-text-center u-mb-md">
        Ce produit a bien été ajouté à votre liste « {{ computedTitre }} »
      </p>
      <div class="u-flex u-flex-align-center u-flex-justify-center u-mb-sm">
        <LazyImage
          :src="article.visuel"
          height="64"
          width="64"
          :dimension="isMobile ? 128 : 64"
          class="u-mr-sm"
        />
        <p class="u-font-13">
          {{ article.designation }}
        </p>
      </div>
    </template>
    <template v-else>
      <h2 class="u-font-secondary u-font-24 u-font-700 u-color-primary u-text-center u-mb-sm">
        Ajouter ce produit à une liste
      </h2>
      <div class="u-flex u-flex-align-center u-flex-justify-center u-mb-sm">
        <LazyImage
          :src="article.visuel"
          height="64"
          width="64"
          :dimension="isMobile ? 128 : 64"
          class="u-mr-sm"
        />
        <p class="u-font-15">
          {{ article.designation }}
        </p>
      </div>
      <Divider dotted />
      <Form
        :error="error"
        @submit.prevent="saveListe"
      >
        <template v-if="listes.length === 0">
          <p class="u-font-15 u-text-center u-mt-md">
            Créez votre première liste dès maintenant !
          </p>
          <div class="u-flex u-flex-column u-flex-align-center u-mt-md u-mb-md">
            <FormGroup
              class="u-flex-justify-center"
              :state="getState(v$,'titre')"
            >
              <FormInput
                v-model="titre"
                :state="getState(v$,'titre')"
                @input="liste = null"
              >
                Nommer cette liste
              </FormInput>
              <template #message>
                <p class="u-font-14">
                  {{ getErrorMessage("titre","Merci de renseigner un titre") }}
                </p>
              </template>
            </FormGroup>
          </div>

          <div class="u-mr-lg u-ml-lg u-mt-md u-mb-xlg u-text-center">
            <Button
              variant="primary"
              radius="full"
              size="md"
              type="submit"
              value="Ajouter à ma liste"
              :disabled="!liste && !titre"
            />
          </div>
        </template>
        <template v-else>
          <div class="u-flex u-flex-justify-between u-mt-md u-mb-md u-flex-column@sm">
            <FormGroup class="u-flex-item-fluid">
              <label :for="formSelect?.$el?.id">
                <p class="u-font-15 u-font-700  u-mb-xs">
                  Sélectionner une liste existante
                </p>
              </label>
              <FormSelect
                ref="formSelect"
                v-uid
                v-model="liste"
                default-value="Choisir une liste"
              >
                <FormSelectOption
                  v-for="listeItem in listes"
                  :key="listeItem.id"
                  :value="listeItem.id"
                >
                  {{ listeItem.attributes.titre }}
                </FormSelectOption>
              </FormSelect>
            </FormGroup>
            <p class="u-font-500 u-mt-lg u-mb-lg u-ml-sm u-mr-sm u-text-center@sm">
              OU
            </p>
            <FormGroup
              :state="getState(v$,'titre')"
              class="u-flex-item-fluid u-flex u-flex-column"
            >
              <p class="u-font-15 u-font-700 u-mb-xs">
                Créer une nouvelle liste
              </p>
              <FormInput
                v-model="titre"
                :state="getState(v$,'titre')"
                @input="liste = null"
              >
                Nommer cette liste
              </FormInput>
              <template #message>
                <p class="u-font-14">
                  {{ getErrorMessage("titre","Merci de renseigner un titre") }}
                </p>
              </template>
            </FormGroup>
          </div>
          <div class="u-mr-lg u-ml-lg u-mt-md u-mb-xlg u-text-center">
            <Button
              variant="primary"
              radius="full"
              size="md"
              type="submit"
              value="Ajouter à ma liste"
              :disabled="!liste && !titre"
            />
          </div>
        </template>
      </Form>
    </template>
  </StateOverlay>
</template>

<style lang="scss" scoped>
</style>
